import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import ProtectedRoute from '../../routes/protected';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { solid } from '@fortawesome/fontawesome-svg-core/import.macro';
import { Toast } from 'primereact/toast';
import { Chip } from 'primereact/chip';
import {
    Chart as ChartJS,
    ArcElement,
    BarElement,
    CategoryScale,
    LinearScale,
    Tooltip,
    Legend,
} from 'chart.js';
import { Doughnut, Bar } from 'react-chartjs-2';
import UsePerms from '../../context/userAccess';
import useCompanyData from '../../context/company';
import surveyService from '../../services/data/survey.service';
import SurveyPageHeader from './SurveyPageHeader';

ChartJS.register(ArcElement, BarElement, CategoryScale, LinearScale, Tooltip, Legend);

const STATUS_CHIP = {
    SC: 'bg-secondary text-white border-0',
    AC: 'bg-success text-white border-0',
    CL: 'bg-dark text-white border-0',
};

const CHART_GREEN = '#5cb75b';
const CHART_GREEN_LIGHT = '#8ec9cf80';
const CHART_GREEN_DARK = '#d96d52';
const CHART_GREEN_MUTED = '#c5e0e3';

const STATUS_COLORS = {
    SC: CHART_GREEN_LIGHT,
    AC: CHART_GREEN,
    CL: CHART_GREEN_DARK,
};

const DISPATCH_COLORS = {
    pendente: CHART_GREEN_LIGHT,
    respondida: CHART_GREEN,
    expirada: CHART_GREEN_DARK,
};

function errDetail(err) {
    const d = err.response?.data?.detail;
    if (Array.isArray(d)) return d.join(' ');
    if (typeof d === 'string') return d;
    return err.message || 'Erro';
}

function KpiCard({ to, icon, iconColor, label, value, sub }) {
    const inner = (
        <div className="card border-clean h-100 hover-shadow-sm">
            <div className="card-body d-flex align-items-center gap-3">
                <div
                    className="rounded-circle d-flex align-items-center justify-content-center flex-shrink-0"
                    style={{ width: 48, height: 48, backgroundColor: `${iconColor}22` }}
                >
                    <FontAwesomeIcon icon={icon} style={{ color: iconColor, fontSize: '1.25rem' }} />
                </div>
                <div>
                    <div className="text-muted small">{label}</div>
                    <div className="fs-4 fw-bold text-default mb-0">{value}</div>
                    {sub ? <div className="text-muted small">{sub}</div> : null}
                </div>
            </div>
        </div>
    );
    if (to) {
        return (
            <Link to={to} className="text-decoration-none text-default">
                {inner}
            </Link>
        );
    }
    return inner;
}

function ChartCard({ title, children }) {
    return (
        <div className="card border-clean h-100">
            <div className="card-body">
                <h6 className="fw-bold text-default mb-3">{title}</h6>
                {children}
            </div>
        </div>
    );
}

function NavDropdown({ label, icon, items }) {
    if (!items.length) return null;
    return (
        <div className="dropdown">
            <button
                type="button"
                className="btn btn-outline-success btn-rounded btn-sm px-3 dropdown-toggle"
                data-bs-toggle="dropdown"
                aria-expanded="false"
            >
                <FontAwesomeIcon icon={icon} className="me-1" />
                {label}
            </button>
            <ul className="dropdown-menu border-clean headerBorder font1em">
                {items.map((item) => (
                    <li key={item.to}>
                        <Link to={item.to} className="dropdown-item text-default">
                            <FontAwesomeIcon icon={item.icon} className="me-2 text-muted" />
                            {item.label}
                        </Link>
                    </li>
                ))}
            </ul>
        </div>
    );
}

export default function SurveyModuleDashboard() {
    const toast = React.useRef(null);
    const VerifyPerms = UsePerms();
    const { rootUser } = useCompanyData();
    const companyId = localStorage.getItem('companyId');
    const canView = VerifyPerms(['view_surveytemplate']) || rootUser;
    const canCreateTemplate = VerifyPerms(['add_surveytemplate']) || rootUser;
    const canViewSurvey = VerifyPerms(['view_survey']) || rootUser;
    const canCreateSurvey = VerifyPerms(['add_survey']) || rootUser;

    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (!companyId || !canView) {
            setLoading(false);
            return;
        }
        setLoading(true);
        surveyService.getModuleOverview({ company_id: companyId })
            .then(setData)
            .catch((err) => {
                toast.current?.show({ severity: 'error', summary: 'Erro', detail: errDetail(err) });
            })
            .finally(() => setLoading(false));
    }, [companyId, canView]);

    if (!canView) {
        return (
            <ProtectedRoute>
                <div className="container-fluid">
                    <SurveyPageHeader title="Dashboard" />
                    <div className="alert alert-warning mx-5 my-4">
                        Sem permissão para acessar o dashboard de pesquisas.
                    </div>
                </div>
            </ProtectedRoute>
        );
    }

    const statusChartData = data ? {
        labels: data.by_status.map((x) => x.label),
        datasets: [{
            data: data.by_status.map((x) => x.count),
            backgroundColor: data.by_status.map((x) => STATUS_COLORS[x.status] || CHART_GREEN_MUTED),
            borderWidth: 0,
        }],
    } : null;

    const autoChartData = data ? {
        labels: data.auto_vs_manual.map((x) => x.label),
        datasets: [{
            data: data.auto_vs_manual.map((x) => x.count),
            backgroundColor: [CHART_GREEN, CHART_GREEN_MUTED],
            borderWidth: 0,
        }],
    } : null;

    const dispatchChartData = data ? {
        labels: data.dispatch_by_status.map((x) => x.label),
        datasets: [{
            label: 'Disparos',
            data: data.dispatch_by_status.map((x) => x.count),
            backgroundColor: data.dispatch_by_status.map((x) => DISPATCH_COLORS[x.status] || CHART_GREEN_MUTED),
            borderRadius: 4,
        }],
    } : null;

    const chartOpts = {
        maintainAspectRatio: false,
        plugins: { legend: { position: 'bottom', labels: { boxWidth: 12, font: { size: 11 } } } },
    };

    const barOpts = {
        ...chartOpts,
        plugins: { legend: { display: false } },
        scales: {
            y: { beginAtZero: true, ticks: { stepSize: 1 } },
        },
    };

    const templateNavItems = [
        { to: '/survey/template/list/', label: 'Ver templates', icon: solid('list') },
        ...(canCreateTemplate
            ? [{ to: '/survey/template/create/', label: 'Criar template', icon: solid('plus') }]
            : []),
    ];

    const surveyNavItems = [
        ...(canViewSurvey
            ? [{ to: '/survey/list/', label: 'Ver pesquisas', icon: solid('list') }]
            : []),
        ...(canCreateSurvey
            ? [{ to: '/survey/create/', label: 'Criar pesquisa', icon: solid('plus') }]
            : []),
    ];

    return (
        <ProtectedRoute>
            <Toast ref={toast} position="top-right" />
            <div className="container-fluid" style={{ paddingBottom: '120px' }}>
                <SurveyPageHeader title="Dashboard" />
                <div className="my-4 mx-5 py-3">
                    <div className="d-flex flex-wrap justify-content-between align-items-center mb-4 gap-2">
                        <div className="d-flex flex-wrap gap-2">
                            <NavDropdown label="Templates" icon={solid('file-lines')} items={templateNavItems} />
                            <NavDropdown label="Pesquisas" icon={solid('clipboard-list')} items={surveyNavItems} />
                            <Link to="/survey/trigger/monitor/" className="btn btn-outline-success btn-rounded btn-sm px-3">
                                <FontAwesomeIcon icon={solid('bolt')} className="me-1" /> Disparos automáticos
                            </Link>
                        </div>
                    </div>

                    {loading && (
                        <div className="text-center py-5 text-muted">Carregando indicadores…</div>
                    )}

                    {!loading && data && (
                        <>
                            <div className="row g-3 mb-4">
                                <div className="col-6 col-md-4 col-xl-2">
                                    <KpiCard
                                        to="/survey/template/list/"
                                        icon={solid('file-lines')}
                                        iconColor="#51A8B1"
                                        label="Templates"
                                        value={data.templates_count}
                                    />
                                </div>
                                <div className="col-6 col-md-4 col-xl-2">
                                    <KpiCard
                                        to="/survey/list/"
                                        icon={solid('clipboard-list')}
                                        iconColor="#5bb75b"
                                        label="Pesquisas"
                                        value={data.surveys_total}
                                        sub={`${data.surveys_draft} rascunho`}
                                    />
                                </div>
                                <div className="col-6 col-md-4 col-xl-2">
                                    <KpiCard
                                        to="/survey/list/"
                                        icon={solid('circle-play')}
                                        iconColor="#5bb75b"
                                        label="Ativas"
                                        value={data.surveys_active}
                                    />
                                </div>
                                <div className="col-6 col-md-4 col-xl-2">
                                    <KpiCard
                                        to="/survey/list/"
                                        icon={solid('circle-stop')}
                                        iconColor="#6c757d"
                                        label="Encerradas"
                                        value={data.surveys_closed}
                                    />
                                </div>
                                <div className="col-6 col-md-4 col-xl-2">
                                    <KpiCard
                                        to="/survey/trigger/monitor/"
                                        icon={solid('bolt')}
                                        iconColor="#17a2b8"
                                        label="Disparo automático"
                                        value={data.surveys_auto_dispatch}
                                        sub={`${data.triggers_active} gatilhos`}
                                    />
                                </div>
                                <div className="col-6 col-md-4 col-xl-2">
                                    <KpiCard
                                        icon={solid('percent')}
                                        iconColor="#f0ad4e"
                                        label="Taxa média (ativas)"
                                        value={`${data.avg_response_rate_active}%`}
                                    />
                                </div>
                            </div>

                            <div className="row g-3 mb-4">
                                <div className="col-md-4">
                                    <ChartCard title="Pesquisas por status">
                                        <div style={{ height: 220 }}>
                                            {statusChartData && (
                                                <Doughnut data={statusChartData} options={{ ...chartOpts, cutout: '55%' }} />
                                            )}
                                        </div>
                                    </ChartCard>
                                </div>
                                <div className="col-md-4">
                                    <ChartCard title="Disparo automático vs manual">
                                        <div style={{ height: 220 }}>
                                            {autoChartData && (
                                                <Doughnut data={autoChartData} options={{ ...chartOpts, cutout: '55%' }} />
                                            )}
                                        </div>
                                    </ChartCard>
                                </div>
                                <div className="col-md-4">
                                    <ChartCard title="Disparos automáticos por status">
                                        <div style={{ height: 220 }}>
                                            {dispatchChartData && (
                                                <Bar data={dispatchChartData} options={barOpts} />
                                            )}
                                        </div>
                                    </ChartCard>
                                </div>
                            </div>

                            <div className="row g-3">
                                <div className="col-lg-7">
                                    <div className="card border-clean">
                                        <div className="card-body">
                                            <div className="d-flex justify-content-between align-items-center mb-3">
                                                <h6 className="fw-bold text-default mb-0">Pesquisas recentes</h6>
                                                <Link to="/survey/list/" className="small text-green">Ver todas</Link>
                                            </div>
                                            {data.recent_surveys.length === 0 ? (
                                                <p className="text-muted small mb-0">Nenhuma pesquisa cadastrada.</p>
                                            ) : (
                                                <div className="table-responsive">
                                                    <table className="table table-sm table-hover align-middle mb-0">
                                                        <thead>
                                                            <tr>
                                                                <th>Nome</th>
                                                                <th>Status</th>
                                                                <th className="text-center">Respostas</th>
                                                                <th></th>
                                                            </tr>
                                                        </thead>
                                                        <tbody>
                                                            {data.recent_surveys.map((s) => (
                                                                <tr key={s.id}>
                                                                    <td>
                                                                        <div className="fw-medium">{s.name}</div>
                                                                        <small className="text-muted">{s.template_name}</small>
                                                                    </td>
                                                                    <td>
                                                                        <Chip
                                                                            label={s.status_display}
                                                                            className={STATUS_CHIP[s.status] || 'bg-secondary text-white border-0'}
                                                                        />
                                                                        {s.auto_dispatch_enabled && s.active_triggers_count > 0 && (
                                                                            <Chip label="Auto" className="bg-info text-white border-0 ms-1" style={{ fontSize: '0.65rem' }} />
                                                                        )}
                                                                    </td>
                                                                    <td className="text-center small">
                                                                        {s.response_count}
                                                                        {s.total_eligible > 0 ? ` / ${s.total_eligible}` : ''}
                                                                    </td>
                                                                    <td className="text-end">
                                                                        <Link to={`/survey/dashboard/${s.id}/`} className="btn btn-sm btn-outline-success btn-rounded">
                                                                            Indicadores
                                                                        </Link>
                                                                    </td>
                                                                </tr>
                                                            ))}
                                                        </tbody>
                                                    </table>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>

                                <div className="col-lg-5">
                                    <div className="card border-clean h-100">
                                        <div className="card-body">
                                            <div className="d-flex justify-content-between align-items-center mb-3">
                                                <h6 className="fw-bold text-default mb-0">Com disparo automático</h6>
                                                <Link to="/survey/trigger/monitor/" className="small text-green">Monitorar</Link>
                                            </div>
                                            {data.surveys_with_auto.length === 0 ? (
                                                <p className="text-muted small mb-0">
                                                    Nenhuma pesquisa com gatilhos automáticos ativos.
                                                </p>
                                            ) : (
                                                <ul className="list-group list-group-flush">
                                                    {data.surveys_with_auto.map((s) => (
                                                        <li key={s.id} className="list-group-item px-0 d-flex justify-content-between align-items-center">
                                                            <div>
                                                                <Link to={`/survey/dashboard/${s.id}/`} className="text-green fw-medium text-decoration-none">
                                                                    {s.name}
                                                                </Link>
                                                                <div className="small text-muted">
                                                                    {s.active_triggers_count} gatilho(s) · {s.status_display}
                                                                </div>
                                                            </div>
                                                            <FontAwesomeIcon icon={solid('bolt')} className="text-info" />
                                                        </li>
                                                    ))}
                                                </ul>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </>
                    )}
                </div>
            </div>
        </ProtectedRoute>
    );
}
